/** Device-local clock for EXTENSUS chat. Follows the person, including TZ moves. */

export function readClockZone() {
  const iana = (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC';
  const offsetMin = -new Date().getTimezoneOffset();
  let abbrev = iana;
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: iana, timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    if (part?.value) abbrev = part.value;
  } catch { /* keep iana */ }
  return { iana, offsetMin, abbrev };
}

export function nowStamp() {
  return new Date().toISOString();
}

export function parseChatTime(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  if (/^\d{2}:\d{2}:\d{2}$/.test(s)) {
    const [h, m, sec] = s.split(':').map(Number);
    const d = new Date();
    d.setUTCHours(h, m, sec || 0, 0);
    return d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatChatTime(raw, zone = readClockZone()) {
  const d = parseChatTime(raw);
  if (!d) return String(raw || '');
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone.iana,
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return d.toLocaleTimeString();
  }
}

export function zonesDiffer(a, b) {
  if (!a || !b) return true;
  return a.iana !== b.iana || Number(a.offsetMin) !== Number(b.offsetMin);
}

export function describeZoneShift(from, to) {
  const a = from?.abbrev || from?.iana || 'unknown';
  const b = to?.abbrev || to?.iana || 'unknown';
  const name = to?.iana && to.iana !== from?.iana ? ` ${to.iana}` : '';
  return `Clock moved ${a} → ${b}${name}. Times on this device follow you.`;
}

export function subscribeClockZone(onChange, { intervalMs = 15000 } = {}) {
  if (typeof window === 'undefined') return () => {};
  let last = readClockZone();
  const tick = () => {
    const next = readClockZone();
    if (zonesDiffer(last, next)) {
      const prev = last;
      last = next;
      onChange(next, prev);
    }
  };
  const id = window.setInterval(tick, intervalMs);
  window.addEventListener('focus', tick);
  document.addEventListener('visibilitychange', tick);
  return () => {
    window.clearInterval(id);
    window.removeEventListener('focus', tick);
    document.removeEventListener('visibilitychange', tick);
  };
}
